import React, { useState, useEffect } from 'react';
import { goBackOrHome } from '@/lib/navHelpers';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TherapyChat } from "@/components/TherapyChat";
import { TherappiDisclaimer } from "@/legal/components/TherappiDisclaimer";
import { TherappiModeChooser } from "@/components/TherappiModeChooser";
import { AITierIndicator } from "@/components/AITierIndicator";
import { Heart, Shield, ArrowLeft, User as UserIcon, Settings, LogOut } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PrivacyDashboard from '@/components/PrivacyDashboard';
import { useDisclaimerConsent } from '@/hooks/useDisclaimerConsent';
import { AppHeader } from '@/components/AppHeader';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { useEditableContent } from '@/hooks/useEditableContent';

const Therapy = () => {
  const [user, setUser] = useState<User | null>(null);
  const [showPrivacyDashboard, setShowPrivacyDashboard] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  // Skip mode chooser if coming from correlation explorer with context
  const [showModeChooser, setShowModeChooser] = useState(true);
  const { hasTherapyConsent, acceptTherapyDisclaimer, loading: consentLoading } = useDisclaimerConsent(user?.id);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get initial context from navigation state (e.g., from correlation explorer)
  const initialContext = location.state?.initialContext as string | undefined;
  
  // Editable content
  const welcomeTitle = useEditableContent('therapy.welcome.title', 'Welcome to AI Therappi<sup class="text-[0.6em]">®</sup>', 'Therapy');
  const welcomeDescription = useEditableContent('therapy.welcome.description', 'Before we begin, please read and accept our important disclaimer and terms of use.', 'Therapy');
  const welcomeButton = useEditableContent('therapy.welcome.button', 'Review Disclaimer & Continue', 'Therapy');
  const sessionTitle = useEditableContent('therapy.session.title', 'Welcome to Your Therappi<sup class="text-[0.6em]">®</sup> Session', 'Therapy');
  const sessionDescription = useEditableContent('therapy.session.description', 'This is your private, judgement-free space for reflective conversation. Share whatever is on your mind, and I\'ll provide supportive, empathetic responses.', 'Therapy');
  const badgeConfidential = useEditableContent('therapy.badge.confidential', '100% Confidential', 'Therapy');
  const badgeJudgment = useEditableContent('therapy.badge.judgment', 'Non-Judgmental', 'Therapy');
  const disclaimerLink = useEditableContent('therapy.disclaimer.link', 'Review AI Therappi<sup class="text-[0.6em]">®</sup> Disclaimer & Terms', 'Therapy');

  const handleAcceptDisclaimer = async () => {
    const success = await acceptTherapyDisclaimer();
    if (success) {
      setShowDisclaimer(false);
    }
  };

  const handleCancelDisclaimer = () => {
    setShowDisclaimer(false);
  };

  const handleModeSelect = (mode: string) => {
    setSelectedMode(mode);
    setShowModeChooser(false);
  };

  const handleChangeMode = () => {
    setShowModeChooser(true);
  };

  // Skip mode chooser and go directly to chat if there's initial context
  useEffect(() => {
    if (initialContext) {
      setSelectedMode('general');
      setShowModeChooser(false);
    }
  }, [initialContext]);

  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);
  const handleSignOut = async () => {
    // Log sign out for compliance
    if (user) {
      await supabase.rpc('log_therapy_action', {
        p_user_id: user.id,
        p_action: 'user_logout',
        p_details: {
          timestamp: new Date().toISOString()
        }
      });
    }
    await supabase.auth.signOut();
  };
  if (!user || consentLoading) {
    return <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>;
  }
  const handleGoBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      goBackOrHome(navigate);
    }
  };

  return <div className="min-h-screen bg-background">
      <AppHeader user={user} onSignOut={handleSignOut} />

      {/* Main Content */}
      <main className="container mx-auto px-6 py-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          {!hasTherapyConsent ? (
            /* Show disclaimer first */
            <div className="space-y-6">
              <Card className="p-6 bg-gradient-therapeutic shadow-therapy border-0">
                <div className="text-center">
                  <h2 className="text-2xl font-light text-foreground mb-3">{welcomeTitle}</h2>
                  <p className="text-muted-foreground mb-4 max-w-2xl mx-auto leading-relaxed">
                    {welcomeDescription}
                  </p>
                  <Button 
                    onClick={() => setShowDisclaimer(true)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    {welcomeButton}
                  </Button>
                </div>
              </Card>
            </div>
          ) : (
            /* Show therapy interface after acceptance */
            <>
              {/* Welcome Card */}
              <Card className="mb-8 p-6 bg-gradient-therapeutic shadow-therapy border-0">
                <div className="text-center">
                  <h2 className="text-2xl font-light text-foreground mb-3">{sessionTitle}</h2>
                  <p className="text-muted-foreground mb-2 max-w-2xl mx-auto leading-relaxed">
                    {sessionDescription}
                  </p>
                  <p className="text-sm text-muted-foreground/80 max-w-2xl mx-auto italic mb-4">
                    Therappi understands your timeline - using your <span className="text-primary">past</span> (Sanctuary), <span className="text-primary">present</span> (Check-In) and <span className="text-primary">future</span> (Navigator) data to personalise guidance.
                  </p>
                  
                  <div className="flex flex-wrap justify-center gap-4 text-sm">
                    <div className="flex items-center gap-2 text-foreground bg-background/50 px-3 py-2 rounded-full">
                      <Shield className="h-4 w-4 text-primary" />
                      <span>{badgeConfidential}</span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground bg-background/50 px-3 py-2 rounded-full">
                      <Heart className="h-4 w-4 text-primary" />
                      <span>{badgeJudgment}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {showModeChooser ? (
                /* Mode Chooser */
                <Card className="shadow-therapy border-0 p-8">
                  <TherappiModeChooser 
                    onSelectMode={handleModeSelect}
                    hasTimeline={false}
                  />
                </Card>
              ) : (
                /* Chat Interface */
                <Card className="shadow-therapy border-0 overflow-hidden">
                  <TherapyChat 
                    user={user} 
                    selectedMode={selectedMode || 'general'} 
                    onModeChange={handleChangeMode}
                    initialContext={initialContext}
                  />
                </Card>
              )}

              {/* AI Tier Indicator - below the chat */}
              <div className="mt-6">
                <AITierIndicator variant="card" />
              </div>
              
              {/* Info Card about The Sanctuary */}
              <Card className="mt-6 p-6 bg-gradient-calm border-primary/20">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-foreground mb-2">Want deeper, personalised support?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    The Sanctuary offers advanced reflection modes using your timeline and condition data, plus specialised tools for anxiety, depression, trauma, and more. Your THERAPPI<sup className="text-[0.6em]">®</sup> AI conversation memory is shared across both.
                  </p>
                  <Button 
                    variant="default" 
                    onClick={() => navigate('/sanctuary')}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Explore The Sanctuary
                  </Button>
                </div>
              </Card>
              
              {/* Compact Disclaimer Link */}
              <div className="text-center mt-6">
                <Button 
                  variant="link" 
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowDisclaimer(true)}
                >
                  {disclaimerLink}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Mandatory Therapy Disclaimer */}
        <TherappiDisclaimer 
          isOpen={showDisclaimer}
          onAccept={handleAcceptDisclaimer}
          onCancel={handleCancelDisclaimer}
        />
      </main>
      
      {/* Privacy Dashboard Modal */}
      <PrivacyDashboard user={user} isOpen={showPrivacyDashboard} onClose={() => setShowPrivacyDashboard(false)} />
    </div>;
};
export default Therapy;